import { useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { ProcessingStatus } from '@/types/Document';
import { 
  checkJobStatus, 
  getJobResult, 
  updateDocumentProcessingResults 
} from '@/lib/api-client';

const normalizeProcessingStatus = (raw: unknown): ProcessingStatus => {
  const s = String(raw || '');
  if (s === 'not_found') return 'error';
  // Default/fallback: treat unknown as 'processing' to avoid regressions to not_processed.
  const known: ProcessingStatus[] = [
    'not_processed',
    'preparing',
    'uploading',
    'queued',
    'in_progress',
    'processing',
    'done',
    'error',
  ];
  return (known as string[]).includes(s) ? (s as ProcessingStatus) : 'processing';
};

const processingStatusRank = (status: ProcessingStatus | undefined): number => {
  switch (status) {
    case 'not_processed': return 0;
    case 'preparing': return 1;
    case 'uploading': return 2;
    case 'queued': return 3;
    case 'in_progress': return 4;
    case 'processing': return 4;
    case 'done': return 5;
    case 'error': return 5;
    default: return 0;
  }
};

const isTerminalProcessingStatus = (status: ProcessingStatus | undefined) =>
  status === 'done' || status === 'error';

const shouldAcceptProcessingStatus = (prev: ProcessingStatus | undefined, next: ProcessingStatus): boolean => {
  if (!prev) return true;
  if (isTerminalProcessingStatus(prev)) return false; // never regress after terminal state
  if (isTerminalProcessingStatus(next)) return true;
  return processingStatusRank(next) >= processingStatusRank(prev);
};

interface UseDocumentPollingProps {
  onStatusUpdate: (documentId: string, status: ProcessingStatus) => void;
  onResultUpdate: (documentId: string, result: any) => void;
  onError: (message: string) => void;
  onAuthError: () => void;
  onSuccess: (message: string) => void;
}

export function useDocumentPolling({
  onStatusUpdate,
  onResultUpdate,
  onError,
  onAuthError,
  onSuccess
}: UseDocumentPollingProps) {
  const pollingIntervalsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const inFlightRef = useRef<Map<string, boolean>>(new Map());
  const requestSeqRef = useRef<Map<string, number>>(new Map());
  const lastAcceptedStatusRef = useRef<Map<string, ProcessingStatus>>(new Map());

  const nextRequestSeq = (documentId: string) => {
    const current = requestSeqRef.current.get(documentId) ?? 0;
    const next = current + 1;
    requestSeqRef.current.set(documentId, next);
    return next;
  };

  const stopPolling = useCallback((documentId: string) => {
    const interval = pollingIntervalsRef.current.get(documentId);
    if (interval) {
      clearInterval(interval);
      pollingIntervalsRef.current.delete(documentId);
    }
    inFlightRef.current.delete(documentId);
  }, []);

  const stopAllPolling = useCallback(() => {
    pollingIntervalsRef.current.forEach((interval) => {
      clearInterval(interval);
    });
    pollingIntervalsRef.current.clear();
    inFlightRef.current.clear();
  }, []);

  const startPolling = useCallback((documentId: string, jobId: string) => {
    // Stop any existing polling for this document
    stopPolling(documentId);
    
    // Clear the last accepted status when starting a NEW job
    // This allows re-processing a document to start fresh
    lastAcceptedStatusRef.current.delete(documentId);

    const pollInterval = setInterval(async () => {
      if (inFlightRef.current.get(documentId)) return;
      inFlightRef.current.set(documentId, true);
      const reqId = nextRequestSeq(documentId);
      try {
        const statusResponse = await checkJobStatus(jobId);
        // Ignore out-of-order responses
        if (requestSeqRef.current.get(documentId) !== reqId) return;

        const normalizedStatus = normalizeProcessingStatus((statusResponse as any)?.status);
        const prevStatus = lastAcceptedStatusRef.current.get(documentId);
        if (!shouldAcceptProcessingStatus(prevStatus, normalizedStatus)) return;
        lastAcceptedStatusRef.current.set(documentId, normalizedStatus);

        onStatusUpdate(documentId, normalizedStatus);
        
        // Update status in MongoDB
        try {
          await updateDocumentProcessingResults(documentId, { 
            status: normalizedStatus
          });
        } catch (updateErr) {
          console.error('Error updating document status during polling:', updateErr);
          
          if (updateErr instanceof Error && updateErr.message.includes('authentication')) {
            onAuthError();
            stopPolling(documentId);
            return;
          }
        }
        
        // If processing is done or errored, handle accordingly
        if (normalizedStatus === 'done' || normalizedStatus === 'error') {
          stopPolling(documentId);

          if (normalizedStatus === 'done') {
            try { 
              const resultResponse = await getJobResult(jobId); 
              onResultUpdate(documentId, resultResponse.result);
              
              const updateData: { status: string; result: any; warnings?: string[] } = { 
                status: 'done', 
                result: resultResponse.result 
              };
              // Forward warnings from the processing API if present
              if (Array.isArray(resultResponse.warnings) && resultResponse.warnings.length > 0) {
                updateData.warnings = resultResponse.warnings;
              }
              await updateDocumentProcessingResults(documentId, updateData);
              onSuccess('Processing complete!');
            } catch (resultError) {
              console.error(`ERROR fetching/updating result for job ${jobId}:`, resultError);
              onError(`Processing finished but failed to fetch/store result: ${resultError instanceof Error ? resultError.message : 'Unknown error'}`);
              onStatusUpdate(documentId, 'error');
            }
          } else if (normalizedStatus === 'error') {
            console.warn(`Polling detected 'error' status for job ${jobId}`);
            onError(`Processing failed for job ${jobId}`);
          }
        } else if ((statusResponse as any)?.status === 'not_found') {
          onError(`Processing failed: Job not found`);
          
          try {
            await updateDocumentProcessingResults(documentId, { 
              status: 'error'
            });
          } catch (updateErr) {
            console.error('Error updating document error status:', updateErr);
          }
          
          stopPolling(documentId);
        }
      } catch (err) {
        Sentry.captureException(err, {
          tags: { flow: 'document-processing', stage: 'polling' },
          extra: { documentId, jobId },
        });
        onError(err instanceof Error ? err.message : 'Error checking job status');
        onStatusUpdate(documentId, 'error');
        
        try {
          await updateDocumentProcessingResults(documentId, { 
            status: 'error'
          });
        } catch (updateErr) {
          console.error('Error updating document status to error during polling:', updateErr);
        }
        
        stopPolling(documentId);
      } finally {
        inFlightRef.current.set(documentId, false);
      }
    }, 2000); // Poll every 2 seconds
    
    pollingIntervalsRef.current.set(documentId, pollInterval);
    
    return pollInterval;
  }, [onStatusUpdate, onResultUpdate, onError, onAuthError, onSuccess, stopPolling]);

  // Reconcile processing statuses for documents with existing jobs
  const reconcileProcessingStatus = useCallback(async (
    documentId: string,
    jobId: string,
    currentStatus: ProcessingStatus,
    currentResult: any | undefined
  ) => {
    const nonFinalStatuses: ProcessingStatus[] = ['queued', 'processing', 'uploading', 'preparing', 'in_progress'];

    if (jobId && nonFinalStatuses.includes(currentStatus)) {
      // If we're already actively polling this document, reconciliation can cause status regressions.
      if (pollingIntervalsRef.current.has(documentId)) return;
      try {
        const reqId = nextRequestSeq(documentId);
        const statusResponse = await checkJobStatus(jobId);
        if (requestSeqRef.current.get(documentId) !== reqId) return;

        const actualStatus = normalizeProcessingStatus((statusResponse as any)?.status);
        
        const prevStatus = lastAcceptedStatusRef.current.get(documentId) ?? currentStatus;
        if (!shouldAcceptProcessingStatus(prevStatus, actualStatus)) return;
        lastAcceptedStatusRef.current.set(documentId, actualStatus);

        onStatusUpdate(documentId, actualStatus);

        if (nonFinalStatuses.includes(actualStatus)) {
          startPolling(documentId, jobId);
        } else if (actualStatus === 'done' && !currentResult) {
          try {
            const resultResponse = await getJobResult(jobId);
            onResultUpdate(documentId, resultResponse.result);
            const updateData: { status: string; result: any; warnings?: string[] } = { status: 'done', result: resultResponse.result };
            if (Array.isArray(resultResponse.warnings) && resultResponse.warnings.length > 0) {
              updateData.warnings = resultResponse.warnings;
            }
            updateDocumentProcessingResults(documentId, updateData)
              .catch(err => console.warn(`Non-critical: Failed to update result in DB:`, err));
          } catch (resultError) {
            console.error(`Error fetching result for completed job ${jobId}:`, resultError);
          }
        } else if (actualStatus === 'error' && currentStatus !== 'error') {
          updateDocumentProcessingResults(documentId, { status: 'error' })
            .catch(err => console.warn(`Non-critical: Failed to update error status in DB:`, err));
        }
      } catch (error) {
        console.error(`Error checking status for job ${jobId} during reconciliation:`, error);
        onStatusUpdate(documentId, 'error');
      }
    }
  }, [onStatusUpdate, onResultUpdate, startPolling]);

  return {
    startPolling,
    stopPolling,
    stopAllPolling,
    reconcileProcessingStatus
  };
}
